'use client';

import { type ReactNode } from 'react';
import {
  Banner,
  Breakpoint,
  Button,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogHeading,
  DialogRoot,
  DialogTrigger,
  Link,
  Spinner,
  Stack,
  Text,
} from '@pie/amber';
import { IconExternalLink } from '@pie/amber-icons';
import { useGenerateStandardCoi } from '@/lib/api/documents';

type Props = {
  children?: ReactNode;
  onClose?: () => void;
  policyId: string;
  open?: boolean;
};

const CertificateOfInsuranceLink = () => {
  return (
    <Link
      external
      href="https://www.pieinsurance.com/blog/workers-comp/workers-compensation-certificate-of-insurance"
      rel="noopener noreferrer"
      target="_blank"
    >
      certificate of insurance (COI)
    </Link>
  );
};

export const StandardCoiDialog = ({ children, onClose, open, policyId }: Props) => {
  const { data, mutate, reset, status } = useGenerateStandardCoi(policyId);

  const handleGenerate = () => {
    mutate();
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (next === false) {
          if (onClose) {
            onClose();
          }

          // need to reset when dialog closes
          reset();
        }
      }}
      position={{ base: 'bottom', sm: 'center' }}
      size="md"
    >
      {children && <DialogTrigger>{children}</DialogTrigger>}
      <DialogContent closeOnOutsideInteraction={status !== 'pending'}>
        {status === 'error' && (
          <>
            <DialogHeader>
              <DialogHeading>Create Certificate of Insurance</DialogHeading>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              <Banner header="Document Generation Failed" size="lg" tone="negative">
                Your certificate of insurance document failed to generate. Please try again
              </Banner>
            </DialogBody>
            <DialogFooter>
              <Stack direction="row" justify="end">
                <Button
                  emphasis="heavy"
                  fluid={{ base: true, sm: false }}
                  onClick={handleGenerate}
                  size={{ base: 'lg', sm: 'md' }}
                  tone="accent"
                >
                  Retry Document Generation
                </Button>
              </Stack>
            </DialogFooter>
          </>
        )}

        {status === 'pending' && (
          <>
            <DialogHeader>
              <DialogHeading>Create Certificate of Insurance</DialogHeading>
            </DialogHeader>
            <DialogBody>
              <Stack align="center" className="py-layout-md" direction="column" gap="2xs">
                <Spinner
                  className="text-text-subtle-default"
                  size="md"
                  title="Generating certificate"
                />
                <Text emphasis="subtle" variant="body-sm">
                  Generating
                </Text>
              </Stack>
            </DialogBody>
          </>
        )}

        {status === 'idle' && (
          <>
            <DialogHeader>
              <DialogHeading>Create Certificate of Insurance</DialogHeading>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              <Text variant="body-sm">
                Create a standard <CertificateOfInsuranceLink /> as proof your business has
                coverage. If you need a custom certificate with specific certificate holder
                information, contact your agent.
              </Text>
            </DialogBody>
            <DialogFooter>
              <Stack direction="row" justify="end">
                <Button
                  emphasis="heavy"
                  fluid={{ base: true, sm: false }}
                  onClick={handleGenerate}
                  size={{ base: 'lg', sm: 'md' }}
                  tone="accent"
                >
                  Create Certificate
                </Button>
              </Stack>
            </DialogFooter>
          </>
        )}

        {status === 'success' && (
          <>
            <DialogHeader>
              <DialogHeading>Create Certificate of Insurance</DialogHeading>
              <DialogClose />
            </DialogHeader>
            <DialogBody>
              <Banner
                actions={
                  <Stack direction={{ base: 'column', md: 'row' }} gap={{ base: 'xs', md: 'sm' }}>
                    <Button
                      as="a"
                      download={data.name}
                      emphasis="heavy"
                      href={`/relay/documents/${data.documentId}/content`}
                      size="md"
                      tone="neutral"
                    >
                      Download Certificate
                    </Button>
                    <Button
                      as="a"
                      emphasis="light"
                      endIcon={<IconExternalLink />}
                      href={`/relay/documents/${data.documentId}/content`}
                      rel="noopener noreferrer"
                      target="_blank"
                      size="md"
                      tone="neutral"
                    >
                      View Certificate
                    </Button>
                  </Stack>
                }
                header="Certificate of Insurance Generated"
                size="lg"
                tone="positive"
              >
                Your COI has been generated and is available in your policy documents.
              </Banner>
            </DialogBody>
            <Breakpoint above="md">
              <DialogFooter>
                <Stack direction="row" justify="end">
                  <DialogClose>
                    <Button emphasis="heavy" size="md" tone="accent">
                      Done
                    </Button>
                  </DialogClose>
                </Stack>
              </DialogFooter>
            </Breakpoint>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
};
